const PictViewClass = require('pict-view');

/** @typedef {import('./HeadlightPanels-View.js').Pict} Pict */

class QRPanel extends PictViewClass
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
		this.samples = [];
		this.location = null;
		this.cameraOpen = false;
		this.removeFromCurrentLocation = pOptions.removeFromCurrentLocation ?? false;
		this.toggleRunning = false;

		this.pict.MetaTemplate.addPattern('{~ParentLocation:', '~}', (pHash, pData, fCallback) =>
		{
			const tmpRecord = this.pict.resolveStateFromAddress(pHash, pData, fCallback);
			return tmpRecord?.ParentLocation ? `<b>Linked to Location:</b> ${ tmpRecord.ParentLocation.Name }` : '';
		});
	}

	/**
	 * Lifecycle hook that triggers after the view is rendered.
	 *
	 * @param {import('pict-view').Renderable} pRenderable - The renderable that was rendered.
	 */
	onAfterRender(pRenderable)
	{
		if (this.cameraOpen)
		{
			this.cameraOpen = false;
			this.pict.providers.QRCodeProvider.closeScanner().catch((e) =>
			{
				console.log('Error stopping qr code scanner: ', e);
			}).finally(() =>
			{
				this.toggleCamera();
			});
		}
		return super.onAfterRender(pRenderable);
	}

	/**
	 * Opens the scanner in the left hand panel.
	 */
	open()
	{
		this.pict.views.HeadlightPanels.handlePanelToggle('QRPanel', 'open');
		this.pict.views.HeadlightPanels.setPanelCustomWidth('left', 40);
		this.samples = [];
		this.location = null;
		this.pict.providers.QRCodeProvider.startWorkflow(this.qrEventHandler);
	}

	closeHandler()
	{
		console.log('Closing QR Code Panel.');
		this.samples = [];
		this.location = null;
		this.cameraOpen = false;
		this.pict.providers.QRCodeProvider.stopWorkflow();
		this.pict.providers.QRCodeProvider.closeScanner();
	}

	qrEventHandler = async (e) =>
	{
		console.log('[QRPanel] Received QREvent: ', e);
		if (e.event === 'scanned-Sample')
		{
			if (e.payload.IDSample && !this.samples.map((s) => s.IDSample).includes(e.payload.IDSample))
			{
				this.samples.push(e.payload);
				this.renderAsync();
			}
		}
		if (e.event === 'scanned-Location')
		{
			if (e.payload.IDLocation && this.location?.IDLocation !== e.payload.IDLocation)
			{
				this.location = e.payload;
				this.renderAsync();
			}
		}
		if (e.event === 'submit')
		{
			this.handleSubmit();
		}
		if (e.event === 'clear')
		{
			this.handleClear();
		}
	};

	/**
	 * Removes a sample from the cart by ID.
	 * @param {Number} IDSample
	 */
	removeSample(IDSample)
	{
		this.samples = this.samples.filter((s) =>
		{
			return s.IDSample !== IDSample;
		});
		this.renderAsync();
	}

	/**
	 * Clears the cart.
	 */
	handleClear()
	{
		this.location = null;
		this.samples = [];
		this.renderAsync();
	}

	/**
	 * Updates the cart of samples to the new selected location.
	 */
	async handleSubmit()
	{
		if (this.location?.IDLocation && this.samples?.length)
		{
			this.pict.views.HeadlightPanels.toast({
				message: 'Updating location for all samples in cart...',
				type: 'is-success',
			});
			try
			{
				for (const s of this.samples)
				{
					await this.pict.providers.LocationProvider.addSampleToLocation(s.IDSample, this.location.IDLocation);
					if (this.removeFromCurrentLocation)
					{
						const currentSampleLocations = await this.pict.providers.LocationProvider.getLocationsBySampleID(s.IDSample);
						for (const l of currentSampleLocations)
						{
							if (l.IDLocation !== this.location.IDLocation)
							{
								await this.pict.providers.LocationProvider.removeSamplefromLocation(s.IDSample, l.IDLocation);
							}
						}
					}
					const tmpLogMessage = `Sample Location updated to ${ this.location.Name }`;
					const geoCoords = await this.pict.providers.GeoProvider.getGeoCoords();
					const tmpOptions = {
						IDSample: s.IDSample,
						CaptureLatitude: geoCoords.CaptureLatitude || s.CaptureLatitude,
						CaptureLongitude: geoCoords.CaptureLongitude || s.CaptureLongitude,
						Notes: tmpLogMessage,
					};
					await this.pict.providers.SampleLogProvider.createSampleLog(tmpOptions);
				}
				this.location = null;
			}
			catch (e)
			{
				console.log('Error adding Sample to Location: ', e);
				this.pict.views.HeadlightPanels.toast({
					message: 'Update Failed',
					type: 'is-danger',
				});
			}
		}
		this.renderAsync();
	}

	/**
	 * Opens the camera scanner.
	 */
	async toggleCamera()
	{
		// Don't run this function again until the ui transition is finished.
		if (this.toggleRunning)
		{
			return;
		}
		this.toggleRunning = true;
		this.cameraOpen = !this.cameraOpen;
		if (this.cameraOpen)
		{
			try
			{
				await this.pict.providers.QRCodeProvider.openScanner('QRPanel-ScannerBox', { isPhoneDevice: this.pict.services.HeadlightPanels.isPhoneDevice });
			}
			catch (e)
			{
				console.log('Error starting qr code scanner: ', e);
			}
			document.querySelector('#QRPanel-ScannerBox-Outer').classList.remove('is-hidden');
			document.querySelector('#QRPanel-CameraButton').classList.add('is-close-button');
		}
		else
		{
			try
			{
				await this.pict.providers.QRCodeProvider.closeScanner();
			}
			catch (e)
			{
				console.log('Error starting qr code scanner: ', e);
			}
			document.querySelector('#QRPanel-ScannerBox-Outer').classList.add('is-hidden');
			document.querySelector('#QRPanel-CameraButton').classList.remove('is-close-button');
		}
		this.toggleRunning = false;
	}

	printUtilityCodes()
	{
		const modalTemplate = /*html*/`
			<div id="HeadlightPanels-QRPanel-Modal-Content">
				<style>
					.centered-modal-content
					{
						display: flex;
						justify-content: center;
					}
				</style>
				<div class="panel-content panel-with-tabs-offset centered-modal-content">
					<button id="QRPanel-OpenPrint" class="button is-primary mt-2" onclick="_Pict.providers.QRCodeProvider.print('action/open/cart', { printStyle: 'thin', raw: true, footers: ['HLLOGO Open Scanning Cart']});">
						<span class="icon mr-3">
							<i class="fas fa-fw fa-cart-shopping"></i>
						</span>
						<span class="icon-text">Print "Open Cart" Code</span>
					</button>
					<button id="QRPanel-ClearPrint" class="button is-primary ml-2 mt-2" onclick="_Pict.providers.QRCodeProvider.print('action/clear', { printStyle: 'thin', raw: true, footers: ['HLLOGO Clear Scanning Cart']});">
						<span class="icon mr-3">
							<i class="fas fa-fw fa-cart-circle-xmark"></i>
						</span>
						<span class="icon-text">Print "Clear Cart" Code</span>
					</button>
					<button id="QRPanel-SubmitPrint" class="button is-primary ml-2 mt-2" onclick="_Pict.providers.QRCodeProvider.print('action/submit', { printStyle: 'thin', raw: true, footers: ['HLLOGO Submit Scanning Cart']});">
						<span class="icon mr-3">
							<i class="fas fa-fw fa-cart-shopping-fast"></i>
						</span>
						<span class="icon-text">Print "Submit" Code</span>
					</button>
				</div>
			</div>
		`;
		const printModalOptions = {
			title: 'Print Utility QR Codes',
			modalBodyTemplate: modalTemplate,
			modalFooterTemplate: /*html*/`
				<div class="columns">
					<div id="Footer-ButtonGroup" class="buttons is-right">
						<button id="Footer-Button-Cancel" type="button" class="button is-link is-soft" onclick="_Pict.fable.HeadlightModal.cancelCallback()">
							<span class="icon"><i class="fas fa-fw fa-times"></i></span>
							<span> Close </span>
						</button>
					</div>
				</div>
			`,
			confirmText: '',
			style: 'wide',
			cancelText: 'Cancel',
		};
		this.fable.HeadlightModal.spawnModal(printModalOptions);
	}

	/**
	 * This method is used to handle the closing of the Observation Annotation panel.
	 * @param {Event} [event] - The click event
	 * @return {void}
	 */
	handleClosePanelEvent(event)
	{
		this.pict.views.HeadlightPanels.closeAllUnpinnedPanels();
	}

	/**
	 * Callback to return scanned result object from a successful scan. Other views can use this to quickly get scanned results when they don't care about the cart and just want a scan result.
	 * @param {Object} e - The scanned event object
	 * @return {Promise<Object|null>} - A promise that resolves to the scanned sample object or null if not valid
	 */
	returnScannedResultsObject = async (e) =>
	{
		console.log('[QRPanel] Received QREvent: ', e);
		if (e.event === 'scanned-Sample')
		{
			if (e.payload.IDSample)
			{
				return e.payload;
			}
		}
		return null;
	};

	/**
	 * Print an entity's QR label, loading the customer's label configuration first.
	 *
	 * Without the ensure these buttons print the built-in fallback layout: the panels app never loaded the
	 * printable manifests, so QRCodeProvider.typeConfig was empty and per-customer printer configuration
	 * (PrintStyle, title, field rows/columns) was silently ignored.
	 * @param {String} pEntity - The entity type being printed ('Sample' | 'Location')
	 * @param {String} pQRCode - The entity's QR code
	 * @return {Promise<void>}
	 * @async
	 */
	async printEntityLabel(pEntity, pQRCode)
	{
		if (!pQRCode)
		{
			return;
		}
		await this.pict.providers.QRCodeProvider.ensurePrintableManifests();
		this.pict.providers.QRCodeProvider.printDecoratedEntity(pEntity, pQRCode);
	}
}

module.exports = QRPanel;

module.exports.default_configuration =
{
	ViewIdentifier: 'QRPanel',
	DefaultRenderable: 'QRPanel',
	DefaultDestinationAddress: '#HeadlightPanels-LeftPanel',
	Templates:
	[
		{
			Hash: 'QRPanel-Wrapper',
			Template: /*html*/`
				<article id="HeadlightPanels-QRPanel" class="panel is-active">
					<style>
						.cart-remove
						{
							width: 30px;
							height: 100%;
							display: flex;
							flex-direction: column; 
							justify-content: center;
							background-color: var(--panels-content-block-divider-color);
							color: white;
							position: absolute;
							align-items: center;
							cursor: pointer;
						}
						.cart-remove:hover
						{
							background-color: var(--bulma-danger);
						}
						.clear-cart
						{
							border: 1px solid #3d3d3d;
							color: white;
							background-color: var(--panels-content-block-divider-color);
						}
						.clear-cart:hover
						{
							background-color: var(--bulma-danger);
						}
					</style>
					<p id="HeadlightPanels-QRPanel-Header" class="panel-heading pr-4">
						<button type="button" class="is-pulled-right hl-panel-close-button" aria-label="close" title="Close panel" 
							onclick="_Pict.views.QRPanel.handleClosePanelEvent(event)">
							<span class="icon">
								<i class="fas fa-fw fa-times-circle" aria-hidden="true"></i>
							</span>
						</button>
						<button type="button" class="is-pulled-right hl-panel-pin-button mr-2" aria-label="pin" title="Pin content" onclick="_Pict.views.HeadlightPanels.togglePanelPinState()">
							<span class="icon">
								<i class="fas fa-fw fa-thumbtack" aria-hidden="true"></i>
							</span>
						</button>
						<span class="icon">
							<i id="HeadlightPanels-QRPanel-Header-Icon" class="fas fa-fw fa-qrcode" aria-hidden="true"></i>
						</span>
						<span id="HeadlightPanels-QRPanel-Header-Title" class="mr-3">Scanning Cart</span>
					</p>
					<style>
						.sample-container {
							overflow: scroll;
						}
						.panel-content-samples {
							max-height: calc(100vh - var(--panels-header-height) - var(--panels-navbar-height)) !important;
							overflow-y: scroll !important;
						}
						.icon-text-for-open-button
						{
							display: inline;
						}
						.icon-text-for-close-button
						{
							display: none;
						}
						.is-close-button .icon-text-for-close-button
						{
							display: inline;
						}
						.is-close-button .icon-text-for-open-button
						{
							display: none;
						}
					</style>
					<div id="HeadlightPanels-QRPanel-Samples-Content" class="panel-content panel-content-samples panel-has-nested-scroll-content">
						<div class="columns m-0 is-mobile">
							<div class="column is-6">
								<button type="button" id="QRPanel-CameraButton" class="button is-primary is-fullwidth" onclick="_Pict.views.QRPanel.toggleCamera()">
									<span class="icon mr-3">
										<i class="fas fa-fw fa-camera-viewfinder"></i>
									</span>
									<span class="icon-text icon-text-for-open-button">Open Camera</span>
									<span class="icon-text icon-text-for-close-button">Close Camera</span>
								</button>
							</div>
							<div class="column is-6">
								<button type="button" title="Removes everything from cart" onclick="_Pict.views.QRPanel.handleClear()" class="button is-fullwidth is-primary is-dark">
									<span class="icon mr-3">
										<i class="fas fa-fw fa-cart-circle-xmark"></i>
									</span>
									<span class="icon-text">Clear Cart</span>
								</button>
							</div>
						</div>
						<div class="columns m-0 is-mobile">
							<div class="column pt-1 is-12">
								<button type="button" title="Updates Sample Locations" onclick="_Pict.views.QRPanel.handleSubmit()" class="button is-success is-fullwidth">
									<span class="icon mr-3">
										<i class="fas fa-fw fa-cart-shopping-fast"></i>
									</span>
									<span class="icon-text">Submit (Updates Sample Locations)</span>
								</button>
							</div>								
						</div>
						<div id="QRPanel-SampleScan-Container" class="has-background-text-35 has-text-white" style="max-height: 50vh; overflow-y: scroll;">
							<style>
								@media screen and (max-width: 768px) {
									#QRPanel-ScannerBox__scan_region > video {
										max-height: 180px;
										object-fit: cover;
									}
								}
							</style>
							<div id="QRPanel-ScannerBox-Outer" class="is-hidden">
								<div id="QRPanel-ScannerBox"></div>
							</div>
						</div>
						<div id="QRPanel-Location-Items" class="mt-2">
							{~TIfAbs:QRPanel-Location-NoItem:Context[0].location:Context[0].location^<=^0~}
							{~TIfAbs:QRPanel-Location-Item:Context[0].location:Context[0].location^>^0~}
						</div>
						<div id="Sample-Container" class="sample-container">{~T:QRPanel-Sample-List:Context[0].samples~}</div>
					</div>
				</article>
			`,
		},
		{
			Hash: 'QRPanel-Sample-List',
			Template: /*html*/`
				{~TIfAbs:QRPanel-Sample-Items:Record:Record.length^>^0~}`,
		},
		{
			Hash: 'QRPanel-Sample-Items',
			Template: /*html*/`
			<div id="QRPanel-Sample-Items" class="nested-scroll-content panel-views-height-offset has-sample-header-offset mt-3">
				{~TS:QRPanel-Sample-Item:Record~}
			</div>`,
		},
		{
			Hash: 'QRPanel-Sample-Item',
			Template: /*html*/`
			<div class="panel-block is-block-flex is-align-items-left is-relative p-0">
				<div class="cart-remove" title="Remove Sample from Cart" onclick="_Pict.views.QRPanel.removeSample({~D:Record.IDSample~})">
					<i class="fas fa-fw fa-circle-xmark"></i>
				</div>
				<figure class="image" style="margin-left: 30px;">
					<img class="preview-image" style="max-width: 100px;" onerror="this.onerror = null; this.src='./images/no-location-data-dark.png'" src="{~GoogleMapURL:Record^100x100^~}" alt="Geolocation for sample" />
				</figure>
				<div class="is-block-flex is-align-items-left mx-4 my-2" style="flex-grow: 1">
					<div><b>Sample</b> {~D:Record.Name~}</div>
					<div class="is-size-7 has-text-text-75"><b>Code:</b> {~D:Record.QRCode~} - <b>Status:</b> {~D:Record.Status~}</div>
					<div class="is-size-7 has-text-text-75"><b>Project:</b> {~Entity:Project^Record.IDProject^QRPanel-Item-Project^~}</div>
					<div class="is-size-7 has-text-text-75">
						<span class="has-text-text-60"><b class="has-text-text-80">Created</b> on</span>
						<span class="has-text-text-80">{~DateFormat:Record.CreateDate^MMMM DD, YYYY - hh:mm a^~}</span>
						<span class="has-text-text-60">by</span>
						<span class="has-text-text-80">{~Entity:User^Record.CreatingIDUser^QRPanel-Item-User^~}</span>
					</div>
				</div>
				<button title="Print QR Code" onclick="_Pict.views.QRPanel.printEntityLabel('Sample', '{~D:Record.QRCode~}');" style="transform: translateY(-25px); right: 10px;" class="button is-primary"><span class="icon"><i class="fas fa-fw fa-print"></i></span></button>
			</div>`,
		},
		{
			Hash: 'QRPanel-Location-NoItem',
			Template: /*html*/`
			<div class="panel-block is-block-flex is-align-items-left is-relative p-0" style="height: 120px; background-color: rgb(0, 0, 0, 0.2);">
				<div class="is-block-flex is-align-items-left mx-4 my-2" style="flex-grow: 1">
					<div class="has-text-centered">
						<span class="icon">
							<i class="fa fa-info-circle"></i>
						</span>
						<span class="icon-text">No Location Scanned</span>
					</div>
				</div>
			</div>`,
		},
		{
			Hash: 'QRPanel-Location-Item',
			Template: /*html*/`
			<div class="panel-block is-block-flex is-align-items-left is-relative p-0" style="height: 120px; background-color: rgb(0, 0, 0, 0.2);">	
			<figure class="image">
					<img class="preview-image" style="max-width: 100px;" onerror="this.onerror = null; this.src='./images/no-location-data-dark.png'" src="{~GoogleMapURL:Record^100x100^~}" alt="Map for this Location" />
				</figure>
				<div class="is-block-flex is-align-items-left mx-4 my-2" style="flex-grow: 1">
					<div><b>Location</b> {~D:Record.Name~}</div>
					<div class="is-size-7 has-text-text-75"><b>Description:</b> {~D:Record.Description~}</div>
					<div class="is-size-7 has-text-text-75"><b>Address:</b></div>
					<div class="is-size-7 has-text-text-75">{~D:Record.Address:No Address~}</div>
					<div class="is-size-7 has-text-text-75">{~D:Record.City:No City~}, {~D:Record.State:No State~}</div>
					<div class="is-size-7 has-text-text-75">{~ParentLocation:Record~}</div>
				</div>
				<button title="Print QR Code" onclick="_Pict.views.QRPanel.printEntityLabel('Location', '{~D:Record.QRCode~}');" style="transform: translateY(-30px); right: 10px;" class="button is-primary"><span class="icon"><i class="fas fa-fw fa-print"></i></span></button>
			</div>`,
		},
		{
			Hash: 'QRPanel-Item-User',
			Template: /*html*/`{~D:Record.NameFirst~} {~D:Record.NameLast~}`,
		},
		{
			Hash: 'QRPanel-Item-Project',
			Template: /*html*/`{~D:Record.Name~}`,
		},
	],
	Renderables:
	[
		{
			RenderableHash: 'QRPanel',
			TemplateHash: 'QRPanel-Wrapper',
			DestinationAddress: '#HeadlightPanels-LeftPanel',
			RenderType: 'replace',
		},
	],
};
